/**
 * Base API Client for CoinVAT API
 * Provides common functionality for HTTP requests, error handling, and authentication
 */
import config from '../config';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface RequestConfig extends Omit<RequestInit, 'body'> {
  body?: any;
  useAuth?: boolean;
}

export class ApiClient {
  private baseURL: string;

  constructor(baseURL?: string) {
    this.baseURL = baseURL || config.API_BASE_URL;
  }

  // Token management
  private getStoredToken(): string | null {
    return localStorage.getItem(config.STORAGE_KEYS.ACCESS_TOKEN);
  }

  // Remove unused getStoredRefreshToken method since auth service handles this

  public storeTokens(tokens: { access_token: string; refresh_token: string; expires_in: number }): void {
    localStorage.setItem(config.STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
    localStorage.setItem(config.STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
    
    // Store expiration time
    const expiresAt = Date.now() + (tokens.expires_in * 1000);
    localStorage.setItem(config.STORAGE_KEYS.TOKEN_EXPIRES_AT, expiresAt.toString());
  }

  public clearTokens(): void {
    localStorage.removeItem(config.STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(config.STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(config.STORAGE_KEYS.TOKEN_EXPIRES_AT);
    localStorage.removeItem(config.STORAGE_KEYS.USER_PROFILE);
  }

  private isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem(config.STORAGE_KEYS.TOKEN_EXPIRES_AT);
    if (!expiresAt) return true;
    
    // Add buffer time to refresh token before it expires
    return Date.now() > (parseInt(expiresAt) - config.TOKEN_REFRESH_BUFFER);
  }

  // Main request method
  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    const {
      body,
      useAuth = true,
      headers = {},
      ...otherConfig
    } = config;

    const requestConfig: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      ...otherConfig,
    };

    // Add auth token if needed and available
    if (useAuth) {
      const token = this.getStoredToken();
      if (token) {
        requestConfig.headers = {
          ...requestConfig.headers,
          'Authorization': `Bearer ${token}`,
        };
      }
    }

    // Handle request body
    if (body !== undefined) {
      if (body instanceof FormData) {
        // Remove Content-Type header for FormData to let browser set it with boundary
        delete (requestConfig.headers as any)['Content-Type'];
        requestConfig.body = body;
      } else if (typeof body === 'object') {
        requestConfig.body = JSON.stringify(body);
      } else {
        requestConfig.body = body;
      }
    }

    try {
      const response = await fetch(url, requestConfig);
      
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorData;
        
        try {
          errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        throw new ApiError(errorMessage, response.status, errorData);
      }

      // Handle empty responses (like 204 No Content)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return {} as T;
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        // Return response text for non-JSON responses
        return (await response.text()) as T;
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Network error or server unavailable', 0);
    }
  }

  // HTTP method shortcuts
  async get<T>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: any, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'POST', body });
  }

  async put<T>(endpoint: string, body?: any, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'PUT', body });
  }

  async patch<T>(endpoint: string, body?: any, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'PATCH', body });
  }

  async delete<T>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }

  // Authentication utilities
  public isAuthenticated(): boolean {
    const token = this.getStoredToken();
    return token !== null && !this.isTokenExpired();
  }

  // Auto-refresh token if it's about to expire
  public async ensureValidToken(): Promise<boolean> {
    if (!this.getStoredToken()) return false;
    
    if (this.isTokenExpired()) {
      try {
        // This will need to be implemented in the auth service
        // For now, just clear tokens if expired
        this.clearTokens();
        return false;
      } catch (error) {
        this.clearTokens();
        return false;
      }
    }
    
    return true;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();